import React, { useContext, useState } from 'react'
import { Button, Modal, ListMenu, ListMenuButton, NotificationManagerContext,
   Text, View } from 'oio-react'
import { useParams, useRouteMatch } from 'react-router-dom'
import { MoreIcon } from 'assets/icons'
import appsConstants from 'config/constants/apps'
import { initiativeSettingsUrl } from 'config/constants/urls'
import InitiativeCreate from 'src/apps/Initiative/apps/Create'
import InitiativeDeleteConfirm from 'src/components/Initiative/DeleteConfirm'
import ContentListSearchBox from 'src/components/Settings/ContentListSearchBox'
import ModalRoute from 'src/components/utils/ModalRoute'
import Popover from 'src/components/utils/Popover'
import { useInitiativeList, useOrganization, useUpdateInitiative } from 'src/graphql/hooks'
import InitiativeListItemRow from '~/components/InitiativeListItemRow'

const AppSettingsInitiativeListing = () => {
   const { appId, initiativeStatus } = useParams()
   const match = useRouteMatch()

   const { showNotification } = useContext(NotificationManagerContext)
   const [initiativeForDeletion, setInitiativeForDeletion] = useState(null)
   const [activeSearchValue, setActiveSearchValue] = useState('')

   const { organization } = useOrganization()
   const app = organization.apps.find(a => a.id === appId)
   const currentAppConstants = appsConstants.find(a => a.type === app.type)

   const AppIcon = currentAppConstants.icon
   const appInitiativeTypeIds = organization.initiativeTypes
      .filter(type => type.class === currentAppConstants.initiativeClass && type.enabled)
      .map(type => type.id)

   const initiativeListFilter = {
      typeIds: appInitiativeTypeIds,
      archived: 'exclude',
      drafts: 'include',
      search: activeSearchValue,
      limit: 25
   }

   if (initiativeStatus === 'archived') {
      initiativeListFilter.archived = 'only'
   }

   const { initiativeList, fetchMore, loading, refetch } = useInitiativeList(initiativeListFilter)
   const { updateInitiative } = useUpdateInitiative()

   const handleUpdateInitiative = async (initiativeId, values) => {
      try {
         await updateInitiative({ id: initiativeId }, values)
         showNotification({
            message: 'Changes saved successfully',
            title: 'Success!',
            type: 'success'
         })
         refetch()
      } catch (err) {
         showNotification({
            message: 'An error occurred while saving changes',
            title: 'Error',
            type: 'error'
         })
      }
   }

   const handleLoadMore = () => {
      fetchMore({
         variables: {
            start: initiativeList.listInfo.nextCursor
         },
         updateQuery: (prevResult, { fetchMoreResult }) => {
            if (!fetchMoreResult) {
               return prevResult
            }

            return {
               ...prevResult,
               initiativeList: {
                  ...prevResult.initiativeList,
                  items: [
                     ...prevResult.initiativeList.items,
                     ...fetchMoreResult.initiativeList.items
                  ],
                  listInfo: fetchMoreResult.initiativeList.listInfo
               }
            }
         }
      })
   }

   return (
      <Popover.Provider>
         <View
            width="100%"
            borderRadius="3px"
            backgroundColor="#fff">
            <ContentListSearchBox onSubmit={setActiveSearchValue} />
            {initiativeList.items.length === 0 && (
               <View
                  display="block"
                  textAlign="center"
                  backgroundColor="#fff"
                  width="100%"
                  padding="30px">
                  <Text size="1.5">
                     No items to show here
                  </Text>
               </View>
            )}
            {initiativeList.items.map((initiative, index) => (
               <InitiativeListItemRow
                  key={initiative.id}
                  editUrl={initiativeSettingsUrl(initiative)}
                  dateLastUpdated={initiative.dateLastUpdated}
                  name={initiative.name}
                  privacy={initiative.privacy}
                  action={(
                     <Popover.Anchor meta={initiative} tabIndex={index}>
                        <MoreIcon
                           width="24px"
                           height="24px"
                           color="#aaa"
                        />
                     </Popover.Anchor>
                  )}
                  icon={AppIcon && (
                     <View
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        width="30px"
                        height="30px"
                        borderRadius="3px"
                        backgroundColor="#ddd"
                        marginRight="12px">
                        <AppIcon
                           width="14px"
                           height="14px"
                           color="#fff"
                           strokeWidth="2px"
                        />
                     </View>
                  )}
               />
            ))}
            {initiativeList.listInfo.hasNext && (
               <View
                  width="100%"
                  display="flex"
                  justifyContent="center"
                  padding="12px 0px">
                  <Button
                     color="#000"
                     textColor="#fff"
                     size="xs"
                     onClick={handleLoadMore}
                     name="Load More"
                     mode={loading ? 'loading' : 'normal'}
                     rounded
                  />
               </View>
            )}
            {/* Initiative List Item Popover */}
            <Popover.View
               anchorOriginHorizontal="right"
               anchorOriginVertical="top"
               backgroundColor="#fff"
               borderRadius="3px"
               width="150px">
               {initiative => (
                  <View
                     float="left"
                     width="100%"
                     backgroundColor="#fff"
                     borderRadius="3px"
                     boxShadow="3px 3px 12px rgba(0,0,0,0.2)">
                     <ListMenu buttonSize="md" buttonTextColor="#333">
                        <ListMenuButton
                           name="Edit"
                           linkTo={initiativeSettingsUrl(initiative)}
                        />
                     </ListMenu>
                     <View float="left" width="100%" borderTop="1px solid #e5e5e5">
                        <ListMenu buttonSize="sm">
                           {initiativeStatus === 'archived' && (
                              <ListMenuButton
                                 name="Unarchive"
                                 onClick={() => handleUpdateInitiative(initiative.id, {
                                    archived: false
                                 })}
                              />
                           )}
                           {initiativeStatus !== 'archived' && (
                              <ListMenuButton
                                 name="Archive"
                                 onClick={() => handleUpdateInitiative(initiative.id, {
                                    archived: true
                                 })}
                              />
                           )}
                           <ListMenuButton
                              name="Delete"
                              onClick={() => setInitiativeForDeletion({
                                 id: initiative.id,
                                 name: initiative.name
                              })}
                           />
                        </ListMenu>
                     </View>
                  </View>
               )}
            </Popover.View>
         </View>
         {/* Create Initiative Modal */}
         <ModalRoute path={`${match.path}/create/:initiativeTypeId`}>
            {({ match: modalRouteMatch, open, onCloseComplete, onCloseTrigger }) => (
               <Modal
                  borderRadius="5px"
                  width="300px"
                  onCloseTrigger={onCloseTrigger}
                  onCloseComplete={onCloseComplete}
                  open={open}>
                  <InitiativeCreate
                     initiativeTypeId={modalRouteMatch.params.initiativeTypeId}
                     onCancelButtonClick={onCloseTrigger}
                     onCloseTrigger={onCloseTrigger}
                     onCreate={onCloseTrigger}
                  />
               </Modal>
            )}
         </ModalRoute>
         {/* Delete Initiative Confirmation Modal */}
         {initiativeForDeletion && (
            <Modal
               borderRadius="5px"
               width="90%[a-b] 300px[c-f]"
               onCloseTrigger={() => setInitiativeForDeletion(null)}
               open={initiativeForDeletion}>
               <InitiativeDeleteConfirm
                  initiativeId={initiativeForDeletion.id}
                  initiativeName={initiativeForDeletion.name}
                  onCancelButtonClick={() => setInitiativeForDeletion(null)}
                  onDelete={() => setInitiativeForDeletion(null)}
               />
            </Modal>
         )}
      </Popover.Provider>
   )
}

export default AppSettingsInitiativeListing
